import { useEffect, useMemo, useState, useRef } from 'react'
import { FlaskConical, ShieldCheck, Clock3, Plus, RefreshCw, Play, ExternalLink, Box, FolderOpen, Monitor, Info, ArrowRight, ArrowLeftRight, ArrowUpRight, Zap, CheckCircle2, AlertTriangle, X } from 'lucide-react'
import { useAppStore } from '@/store'
import { getToken } from '@/lib/auth'
import './SandboxPanel.css'

type SandboxRow = {
  sandbox_id: string
  status: string
  created_at: string
  expires_at: string
  name?: string
  purpose?: string | null
}

type EnvironmentState = {
  environment_mode: 'live' | 'sandbox'
  active_sandbox_id: string | null
  sandbox?: SandboxRow | null
  preview_endpoints?: string[]
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken()
  const res = await fetch('/api' + path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers || {}),
    },
  })
  const text = await res.text()
  let body: any = {}
  try { body = text ? JSON.parse(text) : {} } catch { body = { error: text || res.statusText } }
  if (!res.ok) throw new Error(body.message || body.detail || body.error || `Request failed (${res.status})`)
  return body as T
}

function when(value?: string) {
  if (!value) return '—'
  try {
    const date = new Date(value)
    if (!Number.isFinite(date.getTime())) return 'Unavailable'
    return date.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  }
  catch { return value }
}

export function SandboxPanel() {
  const setEnvironmentMode = useAppStore((s) => s.setEnvironmentMode)
  const [sandboxes, setSandboxes] = useState<SandboxRow[]>([])
  const [environment, setEnvironment] = useState<EnvironmentState | null>(null)
  const [loading, setLoading] = useState(true)
  const [working, setWorking] = useState(false)
  const [error, setError] = useState('')
  const [newName, setNewName] = useState('')
  const [newPurpose, setNewPurpose] = useState('')
  const [tab, setTab] = useState<'Overview' | 'Workspaces' | 'Preview' | 'Workspace details'>('Overview')
  const [showCreate, setShowCreate] = useState(false)
  const [ttl, setTtl] = useState(240)
  const [notice, setNotice] = useState('')
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30000)
    return () => window.clearInterval(timer)
  }, [])
  const dialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (showCreate && dialog && !dialog.open) {
      dialog.showModal()
      dialog.querySelector<HTMLInputElement>('#sb-name')?.focus()
    }
    else if (!showCreate && dialog?.open) dialog.close()
  }, [showCreate])

  const activeSandbox = useMemo(
    () => sandboxes.find((s) => s.sandbox_id === environment?.active_sandbox_id) || environment?.sandbox || null,
    [sandboxes, environment],
  )

  const load = async () => {
    setLoading(true); setError('')
    try {
      const [env, list] = await Promise.all([
        apiFetch<EnvironmentState>('/dashboard/sandbox/environment'),
        apiFetch<{ sandboxes: SandboxRow[] }>('/dashboard/sandboxes'),
      ])
      setEnvironment(env)
      setSandboxes(Array.isArray(list.sandboxes) ? list.sandboxes : [])
      setEnvironmentMode(env.environment_mode === 'sandbox' ? 'sandbox' : 'live')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
    const changed = (event: Event) => {
      const next = (event as CustomEvent<EnvironmentState>).detail
      if (next && ['live', 'sandbox'].includes(next.environment_mode)) setEnvironment(next)
    }
    window.addEventListener('ops-sandbox-change', changed)
    return () => window.removeEventListener('ops-sandbox-change', changed)
  }, [])

  const switchEnvironment = async (mode: 'live' | 'sandbox', sandboxId?: string) => {
    if (working) return
    const ok = window.confirm(
      mode === 'sandbox'
        ? 'Enter Sandbox Mode? Production writes will be blocked unless an operation is explicitly sandbox-enabled.'
        : 'Return to Live Mode? Live actions may affect production resources.'
    )
    if (!ok) return

    setWorking(true); setError(''); setNotice('')
    try {
      let targetSandboxId = sandboxId || environment?.active_sandbox_id || sandboxes.find((s) => s.status === 'active' && Date.parse(s.expires_at) > Date.now())?.sandbox_id || null

      if (mode === 'sandbox' && !targetSandboxId) {
        const created = await apiFetch<SandboxRow>('/dashboard/sandboxes', {
          method: 'POST',
          body: JSON.stringify({
            name: 'My Sandbox',
            purpose: 'Personal OPS sandbox workspace',
            ttl_minutes: 360,
            source_ref: 'dashboard-auto',
          }),
        })
        targetSandboxId = created.sandbox_id
        setSandboxes((current) => [created, ...current.filter((s) => s.sandbox_id !== created.sandbox_id)])
      }

      const next = await apiFetch<EnvironmentState>('/dashboard/sandbox/environment', {
        method: 'POST',
        body: JSON.stringify({
          environment_mode: mode,
          sandbox_id: mode === 'sandbox' ? targetSandboxId : null,
        }),
      })
      setEnvironment(next)
      setEnvironmentMode(next.environment_mode)
      window.dispatchEvent(new CustomEvent('ops-sandbox-change', { detail: next }))

      setNotice('Environment confirmed by the server.')
      setShowCreate(false)
      setNewName(''); setNewPurpose('')
      const refreshed = await apiFetch<{ sandboxes: SandboxRow[] }>('/dashboard/sandboxes')
      setSandboxes(Array.isArray(refreshed.sandboxes) ? refreshed.sandboxes : [])
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setWorking(false)
    }
  }

  const createSandbox = async () => {
    const name = newName.trim()
    if (!name || working) return
    setWorking(true); setError(''); setNotice('')
    try {
      const created = await apiFetch<SandboxRow>('/dashboard/sandboxes', {
        method: 'POST',
        body: JSON.stringify({
          name,
          purpose: newPurpose.trim() || null,
          ttl_minutes: ttl,
          source_ref: 'dashboard',
        }),
      })
      setSandboxes((current) => [created, ...current.filter((row) => row.sandbox_id !== created.sandbox_id)])
      setNotice('Workspace created. Selecting its environment…')
      const next = await apiFetch<EnvironmentState>('/dashboard/sandbox/environment', {
        method: 'POST',
        body: JSON.stringify({ environment_mode: 'sandbox', sandbox_id: created.sandbox_id }),
      })
      setEnvironment(next)
      setEnvironmentMode(next.environment_mode)
      window.dispatchEvent(new CustomEvent('ops-sandbox-change', { detail: next }))
      setNotice('Environment confirmed by the server.')
      setShowCreate(false)
      setNewName(''); setNewPurpose('')
      const refreshed = await apiFetch<{ sandboxes: SandboxRow[] }>('/dashboard/sandboxes')
      setSandboxes(Array.isArray(refreshed.sandboxes) ? refreshed.sandboxes : [])
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setWorking(false)
    }
  }


  const sandbox = environment?.environment_mode === 'sandbox'
  const usable = sandboxes.filter((row) => row.status === 'active' && Date.parse(row.expires_at) > now)
  const expired = sandboxes.filter((row) => row.status === 'expired' || (row.status === 'active' && Date.parse(row.expires_at) <= now))
  const activeUsable = !!activeSandbox && activeSandbox.status === 'active' && Date.parse(activeSandbox.expires_at) > now
  const tabs = ['Overview', 'Workspaces', 'Preview', 'Workspace details'] as const
  const badge = (row: SandboxRow) => row.status === 'active' && Date.parse(row.expires_at) <= now ? 'Expired' : row.status.charAt(0).toUpperCase() + row.status.slice(1)
  const endpoints = (environment?.preview_endpoints || []).filter((url) => {
    try { return new URL(url).protocol === 'https:' } catch { return false }
  })
  const workspaceList = (
    <section className="sb-card">
      <div className="sb-card-heading"><div><h2>My workspaces</h2><p>Workspaces visible to your account</p></div><span className="sb-count">{sandboxes.length}</span></div>
      {loading ? <div className="sb-empty" role="status">Loading workspaces…</div> : !sandboxes.length ? <div className="sb-empty"><FolderOpen size={30} /><h3>Your first workspace starts here</h3><p>Create a named workspace for the changes you want to test.</p><button className="sb-button sb-primary" onClick={() => setShowCreate(true)}><Plus size={16} />Create a sandbox</button></div> :
        <div className="sb-workspaces">{sandboxes.map((row) => {
          const selected = sandbox && row.sandbox_id === environment?.active_sandbox_id
          const enterable = row.status === 'active' && Date.parse(row.expires_at) > now
          return <article key={row.sandbox_id} className={'sb-workspace ' + (selected ? 'sb-selected' : '')}>
            <div className="sb-workspace-icon"><Box size={21} /></div>
            <div className="sb-workspace-copy"><div className="sb-row"><h3>{row.name || 'Unnamed workspace'}</h3><span className={'sb-pill ' + (selected ? 'sb-amber' : enterable ? 'sb-green' : '')}>{selected ? 'Selected' : badge(row)}</span></div><p>{row.purpose || 'No purpose added'}</p><span className="sb-meta"><Clock3 size={13} />Expires {when(row.expires_at)}</span></div>
            <button className="sb-button sb-outline" disabled={working || loading || selected || !enterable} onClick={() => void switchEnvironment('sandbox', row.sandbox_id)}><Play size={14} />{selected ? 'Current workspace' : enterable ? 'Enter workspace' : 'Unavailable'}</button>
          </article>
        })}</div>}
    </section>
  )
  const preview = <section className="sb-card"><div className="sb-card-heading"><div><h2>Workspace preview</h2><p>Preview links supplied for your selected workspace</p></div><Monitor size={20} /></div>
    {endpoints.length ? <div className="sb-preview-links">{endpoints.map((url) => <a className="sb-preview-link" href={url} key={url} target="_blank" rel="noreferrer"><ExternalLink size={18} /><span>{url}</span><ArrowUpRight size={16} /></a>)}</div> : <div className="sb-preview-empty"><Monitor size={38} /><h3>No preview available yet</h3><p>{sandbox ? 'Preview links will appear here when available for this workspace.' : 'Enter a workspace to view its available preview links.'}</p></div>}
    <div className="sb-note"><Info size={16} /><span>Preview links open in a new tab. This view does not run commands or deploy changes.</span></div>
  </section>
  const details = <section className="sb-card"><div className="sb-card-heading"><h2>Sandbox information</h2><Info size={20} /></div>
    <dl className="sb-details"><div><dt>Environment</dt><dd>{environment ? sandbox ? 'Sandbox' : 'Live' : 'Unverified'}</dd></div><div><dt>Workspace</dt><dd>{activeSandbox?.name || 'None selected'}</dd></div><div><dt>Lifecycle</dt><dd>{activeSandbox ? badge(activeSandbox) : '—'}</dd></div><div><dt>Created</dt><dd>{when(activeSandbox?.created_at)}</dd></div><div><dt>Expires</dt><dd>{when(activeSandbox?.expires_at)}</dd></div><div><dt>Purpose</dt><dd>{activeSandbox?.purpose || 'Not provided'}</dd></div></dl>
    {activeSandbox && <details className="sb-technical"><summary>Workspace identifier</summary><code>{activeSandbox.sandbox_id}</code></details>}
    <div className="sb-notice"><ShieldCheck size={20} /><div><strong>Governed by OPS</strong><p>Creating a workspace uses your existing permissions and approval controls. Switching environment does not grant additional access.</p></div></div>
  </section>

  return (
    <div className="sandbox-design">
      <header className="sb-page-header"><div className="sb-title"><div className="sb-hero-icon"><FlaskConical size={30} /></div><div><h1>Sandbox</h1><p>Your workspace for building, previewing and reviewing changes</p></div></div>
        <div className="sb-header-controls"><div className="sb-control"><label htmlFor="sb-environment">Environment</label><select id="sb-environment" disabled={working || loading || !environment} value={environment ? sandbox ? 'sandbox' : 'live' : ''} onChange={(e) => void switchEnvironment(e.target.value as 'live' | 'sandbox')}><option value="" disabled>Unverified</option><option value="live">Live</option><option value="sandbox">Sandbox</option></select></div>
          <div className="sb-control"><label htmlFor="sb-workspace-select">Active sandbox</label><select id="sb-workspace-select" value={sandbox ? environment?.active_sandbox_id || '' : ''} disabled={working || loading || !environment || !usable.length} onChange={(e) => void switchEnvironment('sandbox', e.target.value)}><option value="" disabled>{usable.length ? 'Select a workspace' : 'No active workspaces'}</option>{usable.map((row) => <option key={row.sandbox_id} value={row.sandbox_id}>{row.name || row.sandbox_id}</option>)}</select></div>
          <button className="sb-icon-button" aria-label="Refresh Sandbox state" onClick={() => void load()} disabled={loading || working}><RefreshCw size={18} className={loading ? 'animate-spin' : ''} /></button>
        </div>
      </header>

      <div className={'sb-environment-strip ' + (sandbox ? 'sb-strip-sandbox' : 'sb-strip-live')} role="status"><div><ShieldCheck size={17} /><span><strong>{loading && !environment ? 'Checking environment' : !environment ? 'Environment unverified' : sandbox ? 'Sandbox mode' : 'Live mode'}</strong>{environment && <span className="sb-strip-description">{sandbox ? 'Unsupported production writes are blocked by the Gateway.' : 'Live actions may affect production resources.'}</span>}</span></div>{sandbox && <button className="sb-button sb-small" disabled={working || loading} onClick={() => void switchEnvironment('live')}>Return to Live<ArrowRight size={14} /></button>}</div>
      {error && <div className="sb-error" role="alert"><AlertTriangle size={18} /><span>{error}</span><button className="sb-button sb-small" disabled={working || loading} onClick={() => void load()}>Retry</button></div>}
      {notice && <p className="sb-feedback" role="status">{notice}</p>}
      <nav className="sb-navigation" aria-label="Sandbox views">{tabs.map((item) => <button key={item} className={tab === item ? 'sb-tab-active' : ''} aria-current={tab === item ? 'page' : undefined} onClick={() => setTab(item)}>{item}</button>)}</nav>

      <main className="sb-content">
        {tab === 'Overview' && <>
          <div className="sb-overview-grid">
            <section className="sb-card"><div className="sb-card-heading"><div className="sb-icon-tile"><FlaskConical size={24} /></div><span className={'sb-pill ' + (sandbox ? 'sb-amber' : 'sb-green')}>{environment ? sandbox ? 'Sandbox' : 'Live' : 'Unverified'}</span></div><p className="sb-eyebrow">Current environment</p><h2 className="sb-large-title">{environment ? sandbox ? 'Sandbox Mode' : 'Live Mode' : 'Checking environment'}</h2><p className="sb-body-copy">{sandbox ? 'Test changes in your selected workspace.' : 'Choose a workspace before testing your changes.'}</p><button className="sb-button sb-wide sb-outline" disabled={working || loading || !environment} onClick={() => void switchEnvironment(sandbox ? 'live' : 'sandbox')}><ArrowLeftRight size={17} />{sandbox ? 'Switch to Live Environment' : 'Enter Sandbox Environment'}</button><div className="sb-note"><Info size={17} /><span>{sandbox ? 'Operations not explicitly enabled for Sandbox are refused. Your usual permissions still apply.' : 'Entering Sandbox changes your environment, not your permissions.'}</span></div></section>
            <section className="sb-card"><div className="sb-card-heading"><div className="sb-icon-tile"><Box size={24} /></div>{activeSandbox && <span className={'sb-pill ' + (activeUsable ? 'sb-green' : 'sb-amber')}>{badge(activeSandbox)}</span>}</div><p className="sb-eyebrow">Active sandbox</p><h2 className="sb-large-title sb-wrap">{activeSandbox?.name || 'No workspace selected'}</h2><p className="sb-body-copy">{activeSandbox?.purpose || 'Give your work a clear purpose and a dedicated workspace.'}</p><dl className="sb-details sb-compact"><div><dt>Created</dt><dd>{when(activeSandbox?.created_at)}</dd></div><div><dt>Expires</dt><dd>{when(activeSandbox?.expires_at)}</dd></div></dl><button className="sb-button sb-primary sb-wide" disabled={!activeSandbox} onClick={() => setTab('Workspace details')}>View workspace<ArrowRight size={16} /></button></section>
            <section className="sb-card sb-actions"><div className="sb-card-heading"><h2>Quick actions</h2><Zap size={20} /></div><button className="sb-button sb-outline" disabled={working || loading || !environment} onClick={() => setShowCreate(true)}><Plus size={17} />Create new sandbox</button><button className="sb-button sb-outline" onClick={() => setTab('Workspaces')}><FolderOpen size={17} />Browse workspaces</button><button className="sb-button sb-outline" onClick={() => setTab('Preview')}><Monitor size={17} />View preview</button><button className="sb-button sb-outline" disabled={loading || working} onClick={() => void load()}><RefreshCw size={17} />Refresh status</button><p>Actions use your existing OPS controls.</p></section>
          </div>
          <div className="sb-metrics" aria-label="Workspace summary">{[{label:'Visible workspaces',value:sandboxes.length,icon:FolderOpen},{label:'Active lifecycle',value:usable.length,icon:CheckCircle2},{label:'Expired workspaces',value:expired.length,icon:Clock3},{label:'Preview links',value:endpoints.length,icon:Monitor}].map(({label,value,icon:Icon}) => <div className="sb-metric" key={label}><div className="sb-icon-tile"><Icon size={22} /></div><div><strong>{loading ? '—' : value}</strong><span>{label}</span></div></div>)}</div>
          <div className="sb-secondary-grid">{workspaceList}{preview}{details}</div>
        </>}
        {tab === 'Workspaces' && <><div className="sb-view-heading"><div><h2>Your workspaces</h2><p>Choose an active workspace or create one for a new task.</p></div><button className="sb-button sb-primary" disabled={working || loading || !environment} onClick={() => setShowCreate(true)}><Plus size={16} />Create sandbox</button></div>{workspaceList}</>}
        {tab === 'Preview' && preview}
        {tab === 'Workspace details' && details}
      </main>

      <dialog ref={dialogRef} className="sb-dialog" onCancel={(e) => { if (working) e.preventDefault(); else setShowCreate(false) }} onClose={() => setShowCreate(false)} aria-labelledby="sb-create-heading">
        <form onSubmit={(e) => { e.preventDefault(); void createSandbox() }}>
          <div className="sb-card-heading"><div><p className="sb-eyebrow">New workspace</p><h2 id="sb-create-heading">Create a sandbox</h2></div><button type="button" className="sb-icon-button" aria-label="Close create sandbox" disabled={working} onClick={() => setShowCreate(false)}><X size={20} /></button></div>
          <p className="sb-body-copy">Create a workspace and enter Sandbox mode. Existing approval and Worker Lock requirements apply.</p>
          <label htmlFor="sb-name">Workspace name</label><input id="sb-name" autoFocus required maxLength={120} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Dashboard improvements" disabled={working} />
          <label htmlFor="sb-purpose">Purpose <span>(optional)</span></label><textarea id="sb-purpose" maxLength={500} rows={3} value={newPurpose} onChange={(e) => setNewPurpose(e.target.value)} placeholder="What will you build or validate?" disabled={working} />
          <label htmlFor="sb-lifetime">Workspace lifetime</label><select id="sb-lifetime" value={ttl} onChange={(e) => setTtl(Number(e.target.value))} disabled={working}><option value={60}>1 hour</option><option value={120}>2 hours</option><option value={240}>4 hours</option><option value={360}>6 hours</option></select>
          <div className="sb-note"><Clock3 size={16} /><span>The workspace expires after its lifetime. Entering Live mode is a separate, confirmed action.</span></div>
          {error && <p className="sb-error" role="alert">{error}</p>}
          <div className="sb-dialog-footer"><button type="button" className="sb-button sb-outline" disabled={working} onClick={() => setShowCreate(false)}>Cancel</button><button type="submit" className="sb-button sb-primary" disabled={!newName.trim() || working}>{working ? 'Creating…' : 'Create & enter Sandbox'}<ArrowRight size={16} /></button></div>
        </form>
      </dialog>
    </div>
  )
}
